// Phase A — Notary Act Deep Content section
// Renders regulatory update, destination matrix, mistakes, case study, cross-service
// bridges. Mounted at the bottom of /notary/$act and /en/notary/$act to enrich
// all 32 acts × 2 languages in one shot.

import { Link } from "@tanstack/react-router";
import {
  AlertTriangle, Globe2, ShieldCheck, ArrowRight, BookOpen, Landmark,
} from "lucide-react";
import type { NotaryAct } from "@/data/notary-acts";
import {
  TOP_NOTARY_DESTINATIONS, mistakesFor, caseStudyFor,
  REGULATORY_2025, CROSS_BRIDGES,
} from "@/data/notary-act-deep";

interface Props {
  act: NotaryAct;
  lang: "th" | "en";
}

export default function NotaryActDeepContent({ act, lang }: Props) {
  const isTh = lang === "th";
  const mistakes = mistakesFor(act);
  const cs = caseStudyFor(act);

  return (
    <div className="mx-auto max-w-5xl px-4 pb-16 sm:px-6" data-speakable>
      {/* Regulatory 2025-2026 */}
      <section className="mt-14 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 sm:p-8">
        <div className="flex items-start gap-3">
          <Landmark className="mt-1 h-6 w-6 shrink-0 text-amber-700" />
          <div>
            <p className="text-[11px] font-bold uppercase tracking-widest text-amber-700">
              {isTh ? "อัปเดตกฎหมาย 2025-2026" : "Regulatory update 2025-2026"}
            </p>
            <h2 className="mt-1 font-display text-xl font-extrabold text-navy-deep sm:text-2xl">
              {isTh ? "Hague Apostille มีผลกับไทยแล้ว" : "Thailand Apostille — now in force"}
            </h2>
            <p className="mt-3 text-[14px] leading-relaxed text-slate-700 sm:text-[15px]">
              {isTh ? REGULATORY_2025.th : REGULATORY_2025.en}
            </p>
          </div>
        </div>
      </section>

      {/* Destination matrix */}
      <section className="mt-14">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
          {isTh
            ? `${act.nameTh} — ใช้ประเทศไหนได้บ้าง (Top 15)`
            : `${act.nameEn} — accepted destinations (Top 15)`}
        </h2>
        <p className="mt-2 text-sm text-slate-600">
          {isTh
            ? "ประเทศปลายทางที่ลูกค้า NYC ใช้บริการนี้บ่อยที่สุด พร้อมขั้นตอนหลัง Notary (Apostille หรือ Embassy)"
            : "The 15 destinations our clients notarise this act for most often, with the correct post-notary chain."}
        </p>
        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {TOP_NOTARY_DESTINATIONS.map((d) => (
            <article
              key={d.code}
              className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:border-amber-300"
            >
              <div className="flex items-center gap-2">
                <span className="text-xl leading-none" aria-hidden>{d.flag}</span>
                <div className="min-w-0">
                  <p className="truncate font-display text-sm font-extrabold text-navy-deep">
                    {isTh ? d.nameTh : d.nameEn}
                  </p>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-amber-700">
                    {d.chain === "apostille"
                      ? isTh ? "Notary → MFA Apostille" : "Notary → MFA Apostille"
                      : isTh ? "Notary → MFA → Embassy" : "Notary → MFA → Embassy"}
                  </p>
                </div>
              </div>
              <p className="mt-2 text-[12px] leading-relaxed text-slate-600">
                {isTh ? d.noteTh : d.noteEn}
              </p>
            </article>
          ))}
        </div>
      </section>

      {/* Common mistakes */}
      <section className="mt-14 rounded-3xl border border-rose-200 bg-rose-50/40 p-6 sm:p-8">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-1 h-6 w-6 shrink-0 text-rose-600" />
          <div className="min-w-0">
            <h2 className="font-display text-xl font-extrabold text-navy-deep sm:text-2xl">
              {isTh ? "ข้อผิดพลาดที่พบบ่อย" : "Common mistakes to avoid"}
            </h2>
            <ul className="mt-4 grid gap-2.5">
              {mistakes.map((m, i) => (
                <li key={i} className="flex items-start gap-2 text-[14px] leading-relaxed text-slate-700">
                  <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-rose-500" />
                  <span>{isTh ? m.th : m.en}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Micro case study */}
      <section className="mt-14 rounded-3xl border border-slate-200 bg-slate-50/60 p-6 sm:p-8">
        <div className="flex items-start gap-3">
          <BookOpen className="mt-1 h-6 w-6 shrink-0 text-navy-deep" />
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500">
              {isTh ? "เคสตัวอย่างจริง (Anonymised)" : "Real client case (anonymised)"}
            </p>
            <p className="mt-2 text-[14px] italic leading-relaxed text-slate-700 sm:text-[15px]">
              "{isTh ? cs.th : cs.en}"
            </p>
          </div>
        </div>
      </section>

      {/* Cross-service bridges */}
      <section className="mt-14">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
          {isTh ? "บริการที่ควรทำต่อเนื่อง" : "Recommended next-step services"}
        </h2>
        <p className="mt-2 text-sm text-slate-600">
          {isTh
            ? `${act.nameTh} มักไม่จบในตัวเอง — เลือกขั้นตอนถัดไปให้เอกสารใช้งานได้จริงในประเทศปลายทาง`
            : `${act.nameEn} rarely stands alone — pick the correct next step so the document is legally usable at the destination.`}
        </p>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          {CROSS_BRIDGES.map((b) => (
            <Link
              key={b.hrefTh}
              to={isTh ? b.hrefTh : b.hrefEn}
              className="group flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-amber-300 hover:shadow-md"
            >
              <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-1 font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-700">
                  {isTh ? b.toTh : b.toEn}
                  <ArrowRight className="h-3.5 w-3.5 opacity-60 transition group-hover:translate-x-0.5" />
                </p>
                <p className="mt-1 text-[12px] leading-relaxed text-slate-600">
                  {isTh ? b.whyTh : b.whyEn}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Quotable stat strip (AEO) */}
      <section className="mt-10 rounded-2xl border border-navy-deep/20 bg-navy-deep p-5 text-white sm:p-6">
        <Globe2 className="mb-2 h-5 w-5 text-gold" />
        <p className="text-[13px] leading-relaxed text-white/90 sm:text-sm">
          {isTh
            ? `NYC Legal & Notary Service ให้บริการ ${act.nameTh} เฉลี่ย ${act.turnaround} เริ่ม ฿${act.priceFromTh.toLocaleString()} ต่อฉบับ — ดำเนินการโดยทนายผู้ทำคำรับรอง (Notarial Services Attorney) ขึ้นทะเบียนสภาทนายความแห่งประเทศไทย สำนักงานลาดพร้าว 95 กรุงเทพฯ • DBD 0435567000061`
            : `NYC Legal & Notary Service delivers ${act.nameEn} in ${act.turnaround} on average, from ฿${act.priceFromTh.toLocaleString()} per document — performed in person by a Notarial Services Attorney registered with the Lawyers Council of Thailand at our Lat Phrao 95, Bangkok office • DBD 0435567000061.`}
        </p>
      </section>
    </div>
  );
}
